import React, { useEffect, useMemo, useRef, useState } from "react"
import { motion, AnimatePresence } from 'framer-motion'
import { IoIosAlert } from "react-icons/io";
import { GiCheckMark } from 'react-icons/gi'
import { createPortal } from "react-dom";
import { RiArrowDownSLine } from 'react-icons/ri'

const SelectOptionAnim = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
}

const DEFAULT_MENU_HEIGHT = 270
const VIEWPORT_PADDING = 12
const DROPDOWN_OFFSET = 8

const FieldSelect = ({ label, injectClass = "", edit, fieldProps = {}, fieldMeta = {}, fieldHelper, options = [], loading = false, dataLoaded = false, onChange, disabled = false, arrowEnd = false, fullWidth = false, title }) => {
    const input = useRef(null)
    const wrapRef = useRef(null)
    const dropdownRef = useRef(null)
    const [showError, setShowError] = useState(false)
    const [filter, setFilter] = useState('')
    const [focussed, setFocussed] = useState(false)
    const [pos, setPos] = useState({ width: 0, top: 0, left: 0, maxHeight: '270px' })

    const hasError = Boolean(edit && fieldMeta?.error && fieldMeta?.touched)

    const makeFocus = () => {
        if (disabled || !edit) return
        input.current?.focus()
    }

    const toggleFocus = () => {
        if (disabled || !edit) return
        setFocussed(true)
    }

    const handleBlur = () => {
        setTimeout(() => setFocussed(false), 150)
    }

    const setValue = (value) => {
        if (fieldHelper?.setValue) fieldHelper.setValue(value)
        if (typeof onChange === 'function') onChange(value)
        setFilter('')
        setFocussed(false)
    }

    const normalizedOptions = useMemo(() => (Array.isArray(options) ? options : []), [options])

    const filteredOptions = useMemo(() => {
        const searchText = filter.toLowerCase().trim()

        return normalizedOptions.filter((option) => {
            const labelText = option?.text?.toString?.() || ''
            return !searchText || labelText.toLowerCase().includes(searchText)
        })
    }, [filter, normalizedOptions])

    const filteredCount = filteredOptions.length

    const displayText = useMemo(() => {
        const selected = normalizedOptions.find((option) => option.value === fieldProps.value)
        return selected ? selected.text : false
    }, [fieldProps.value, normalizedOptions])

    useEffect(() => {
        const updateDropdownPosition = () => {
            if (!wrapRef.current) return

            const rect = wrapRef.current.getBoundingClientRect()
            const availableHeight = Math.max(
                96,
                window.innerHeight - rect.bottom - VIEWPORT_PADDING - DROPDOWN_OFFSET
            )

            setPos({
                width: `${Math.max(160, rect.width)}px`,
                left: `${Math.max(VIEWPORT_PADDING, rect.left)}px`,
                top: `${rect.bottom + DROPDOWN_OFFSET}px`,
                maxHeight: `${Math.min(DEFAULT_MENU_HEIGHT, availableHeight)}px`,
            })
        }

        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                wrapRef.current &&
                !wrapRef.current.contains(event.target)
            ) {
                setFocussed(false)
            }
        }

        if (focussed) {
            updateDropdownPosition()
            window.addEventListener('scroll', updateDropdownPosition, true)
            window.addEventListener('resize', updateDropdownPosition)
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            window.removeEventListener('scroll', updateDropdownPosition, true)
            window.removeEventListener('resize', updateDropdownPosition)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [focussed, filteredCount])

    return (
        <motion.div className="relative mt-3 mb-2">
            {label ? <div className="mb-2 px-1 text-xs font-bold uppercase tracking-[0.18em] text-tpColor">{label}</div> : null}
            <motion.div className={`relative w-full rounded-[22px] border border-borderColor bg-white/92 px-4 py-3 shadow-soft transition focus-within:border-primeColor/45 ${hasError ? 'border-[#c44545]/50' : ''} ${injectClass}`}>
                <div
                    ref={wrapRef}
                    className={`group relative flex flex-col rounded-lg ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${loading ? 'animate-pulse' : ''}`}
                    onClick={makeFocus}
                >
                    {!dataLoaded ? (
                        <div className="h-6 w-3/4 animate-pulse rounded-lg bg-slate-200"></div>
                    ) : (
                        <>
                            {!focussed && (
                                <div className={`overflow-hidden text-start text-sm font-semibold text-tColor ${title || label || fullWidth ? 'w-full' : 'w-12'}`}>
                                    {displayText || `Select ${label || title || 'option'}`}
                                </div>
                            )}
                            <input
                                ref={input}
                                disabled={disabled}
                                className={`w-full bg-transparent text-sm font-semibold text-tColor outline-none placeholder:text-tpColor/50 ${!focussed ? 'absolute h-0 w-0 opacity-0' : 'h-6'}`}
                                type="text"
                                value={filter}
                                autoComplete="off"
                                onFocus={toggleFocus}
                                onBlur={handleBlur}
                                onChange={(ev) => setFilter(ev.target.value)}
                                placeholder={`Search ${label || title || 'option'}`}
                            />
                            <span className={`${arrowEnd ? 'right-0' : 'right-1'} absolute top-0 flex text-xl`}>
                                {hasError ? (
                                    <IoIosAlert className="cursor-pointer text-[#c44545]" onMouseEnter={() => setShowError(true)} onMouseLeave={() => setShowError(false)} />
                                ) : (
                                    <RiArrowDownSLine className={`text-2xl text-tpColor transition ${focussed ? 'rotate-180' : ''}`} />
                                )}
                            </span>
                        </>
                    )}

                    {showError && (
                        <motion.div className="absolute right-0 top-8 z-20 w-56 rounded-2xl border border-rose-200 bg-white px-3 py-2 text-xs font-semibold leading-5 text-[#c44545] shadow-soft">
                            {fieldMeta.error}
                        </motion.div>
                    )}
                </div>
                {createPortal(
                    <AnimatePresence>
                        {focussed && (
                            <motion.div
                                ref={dropdownRef}
                                variants={SelectOptionAnim}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                                className="fixed z-[200000] overflow-y-auto rounded-[22px] border border-slate-200 bg-white p-2 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.45)] backdrop-blur-0"
                                style={pos}
                            >
                                {filteredOptions.map((option, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => setValue(option.value)}
                                        className={`flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm font-semibold transition hover:bg-primeColor hover:text-white ${option.value === fieldProps.value ? 'bg-primeColor/10 text-primeColor' : 'text-tColor'}`}
                                    >
                                        <span>{option.text}</span>
                                        {option.value === fieldProps.value && <GiCheckMark className="text-base text-sColor" />}
                                    </button>
                                ))}
                                {filteredOptions.length === 0 && <span className="block px-4 py-3 text-sm font-semibold text-tpColor">No options found</span>}
                            </motion.div>
                        )}
                    </AnimatePresence>,
                    document.body
                )}
            </motion.div>
        </motion.div>
    )
}

export default FieldSelect
